/**
 * @file ErrorBoundary.js
 * @description A React error boundary component that catches JavaScript errors anywhere in the component tree,
 * logs those errors, and displays a fallback UI instead of crashing the application.
 */

import React from "react";
import { Box, Button, Typography } from "@mui/material";

/**
 * ErrorBoundary component that catches and handles errors in child components
 * @extends React.Component
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Updates the state when an error is caught
   * @param {Error} error - The error that was caught
   * @returns {Object} New state indicating an error has occurred
   */
  static getDerivedStateFromError(error) {
    // Update state to show fallback UI
    return { hasError: true };
  }

  /**
   * Logs error information when an error is caught
   * @param {Error} error - The error that was caught
   * @param {Object} errorInfo - Additional error information
   */
  componentDidCatch(error, errorInfo) {
    // Log error details to an error reporting service
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  /**
   * Renders either the fallback UI or the child components
   * @returns {JSX.Element} The rendered component
   */
  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "20px",
            textAlign: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Typography variant="h4" gutterBottom>
            Oops! Something went wrong.
          </Typography>
          <Typography variant="body1" gutterBottom>
            We apologize for the inconvenience. Please try again later.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Refresh Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
